/**
 * Service worker update checker.
 *
 * Pure logic factored out of registerServiceWorker.ts so it can be unit
 * tested — the registration module imports `virtual:pwa-register`, which
 * only resolves inside Vite builds (vitest maps it to a recording mock via
 * resolve.alias).
 *
 * The browser runs its own soft update on every full-page navigation, so a
 * page load is always covered. What it does not cover is the rest of the
 * session: this is an SPA, so after boot the routing is client-side and no
 * navigation ever happens again. A long-lived tab — and especially the
 * installed PWA, which never navigates at all — would otherwise keep serving
 * the deploy it booted on indefinitely. This checker supplies the signals the
 * browser no longer does: an interval, and every return to the foreground.
 */

/**
 * Minimum gap between checks so foreground flips don't spam the server.
 *
 * 60s, not the five minutes this started at. The foreground flip is the
 * signal that actually catches a deploy for a tab that will never navigate
 * again, and a five minute window swallowed most of them: a user who glanced
 * at another tab and came back was simply not checked. sw.js is a few KB
 * served no-store, so the worst case here — one small fetch per minute of
 * tab-flipping — is not a real cost, and it buys detection in about a minute
 * instead of up to an hour.
 */
export const MIN_CHECK_GAP_MS = 60 * 1000;

export interface SwUpdateCheckerOptions {
  /**
   * When it returns true, the check is skipped (and does not consume the
   * throttle). Used to avoid triggering the reload-on-update while a
   * private-mode conversation is active — private messages live only in
   * client memory, so an update-driven reload would destroy them.
   */
  shouldSkip?: () => boolean;
}

/**
 * True once update() has turned up a worker that is installing or waiting.
 *
 * Lives outside the checker closure on purpose: inside it, TypeScript still
 * holds the narrowing from the `registration.installing` guard and would
 * treat this read as statically null, even though update() is exactly what
 * repopulates it.
 */
function hasPendingWorker(registration: ServiceWorkerRegistration): boolean {
  return registration.installing !== null || registration.waiting !== null;
}

export function createSwUpdateChecker(
  swUrl: string,
  registration: ServiceWorkerRegistration,
  options: SwUpdateCheckerOptions = {}
): () => Promise<void> {
  // Seed the throttle open so the first check of a session can run straight
  // away. Seeding it to Date.now() charged the session for a check that never
  // happened, and it cost exactly the checks that matter: when a check
  // short-circuits below (private mode at load, offline at load, or the fresh
  // SW still precaching) nothing is written, so the throttle should still be
  // open for the very next foreground flip — with the old seed that flip was
  // swallowed and the tab waited out the whole window. The `installing` guard
  // below — not this seed — is what keeps a precaching SW alone.
  let lastCheckAt = Number.NEGATIVE_INFINITY;

  return async function checkForSwUpdate(): Promise<void> {
    const now = Date.now();
    if (now - lastCheckAt < MIN_CHECK_GAP_MS) return;
    if (options.shouldSkip?.()) return;
    if (registration.installing) return;
    if ('onLine' in navigator && !navigator.onLine) return;

    lastCheckAt = now;
    try {
      // Probe the SW script first (per vite-pwa guidance): update() on an
      // unreachable server or captive portal can mark the registration
      // redundant, so only run it when the script is actually served.
      const resp = await fetch(swUrl, {
        cache: 'no-store',
        headers: { 'cache-control': 'no-cache' },
      });
      // Release the probe body — only the status matters.
      void resp.body?.cancel();
      if (resp.status === 200) {
        await registration.update();
        // Only speak up when the check actually turned something up — a
        // routine no-op check stays silent. Without this line a "it served me
        // a stale bundle" report cannot be answered from the console.
        if (hasPendingWorker(registration)) {
          console.info('[sw] update check found a new service worker');
        }
      }
    } catch {
      // Network hiccup — the next interval or foreground event retries.
    }
  };
}
